import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import api from '../api';

const mobileCSS = `
  @media (max-width: 768px) {
    .ms-shell { flex-direction: column !important; }
    .ms-sidebar {
      width: 100% !important; height: 52px !important;
      flex-direction: row !important; padding: 0 !important;
      position: fixed !important; bottom: 0 !important; left: 0 !important;
      right: 0 !important; z-index: 100 !important;
      border-right: none !important; border-top: 1px solid rgba(255,255,255,0.1) !important;
    }
    .ms-logo-area { display: none !important; }
    .ms-nav {
      display: flex !important; flex-direction: row !important;
      padding: 0 8px !important; align-items: center !important;
      justify-content: space-around !important; width: 100% !important;
      overflow-x: auto !important;
    }
    .ms-nav-section { display: none !important; }
    .ms-nav-item {
      flex-direction: column !important; gap: 2px !important;
      padding: 6px 12px !important; font-size: 10px !important;
      border-right: none !important; border-bottom: none !important;
      min-width: 56px !important; text-align: center !important;
    }
    .ms-nav-icon { font-size: 18px !important; }
    .ms-nav-label { font-size: 9px !important; }
    .ms-bottom { display: none !important; }
    .ms-main { padding-bottom: 56px !important; }
    .ms-content { padding: 12px 12px !important; }
    .ms-health-grid { grid-template-columns: repeat(2, 1fr) !important; }
    .ms-mixer-grid { grid-template-columns: 1fr !important; }
    .ms-tabs { overflow-x: auto !important; }
  }
  @media (max-width: 480px) {
    .ms-nav-item { padding: 6px 8px !important; min-width: 44px !important; }
    .ms-nav-label { display: none !important; }
  }
`;

export default function NavBar({ children, liveStatus: propLiveStatus }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [liveStatus, setLiveStatus] = useState(propLiveStatus || null);
  const [stationLogo, setStationLogo] = useState('');

  useEffect(() => {
    api.get('/settings/logo', {responseType:'blob'}).then(r => {
      setStationLogo(URL.createObjectURL(r.data));
    }).catch(() => {});
  }, []);
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  useEffect(() => {
    const load = async () => {
      try { const { data } = await api.get('/stream/status'); setLiveStatus(data); } catch {}
    };
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: '▦' },
    { path: '/recordings', label: 'Recordings', icon: '⏺' },
    { path: '/production', label: 'Production', icon: '🎚', newTab: true },
    { path: '/security', label: 'Security', icon: '🛡' },
    { path: '/guest-studio', label: 'Guest Studio', icon: '🎙' },
    { path: '/settings', label: 'Settings', icon: '⚙' },
  ];
  if (user?.role === 'admin') navItems.push({ path: '/admin', label: 'Users', icon: '👥' });

  const roleColor = { admin: 'var(--signal)', manager: 'var(--warn)', dj: '#9146ff' }[user?.role] || 'var(--text-muted)';

  const s = {
    shell: { display: 'flex', height: '100vh', overflow: 'hidden' },
    sidebar: {
      width: '210px', flexShrink: 0,
      background: '#4A1B0C',
      display: 'flex', flexDirection: 'column',
      borderRight: '1px solid rgba(0,0,0,0.2)',
    },
    logoArea: {
      padding: '20px 18px 18px',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
    },
    logoRow: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '4px' },
    logoMark: {
      width: '30px', height: '30px', borderRadius: '8px',
      background: 'var(--signal)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '14px', fontWeight: '800', color: '#4A1B0C', flexShrink: 0,
    },
    logoText: { fontWeight: '800', fontSize: '14px', color: '#fff', lineHeight: 1.1 },
    logoSub: { fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginLeft: '40px' },
    liveBadge: {
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      fontSize: '10px', fontWeight: '700',
      color: liveStatus?.live ? '#2ECC71' : 'rgba(255,255,255,0.3)',
      marginTop: '10px',
    },
    liveDot: {
      width: '6px', height: '6px', borderRadius: '50%',
      background: liveStatus?.live ? '#2ECC71' : 'rgba(255,255,255,0.2)',
      animation: liveStatus?.live ? 'pulse-dot 2s infinite' : 'none',
    },
    nav: { flex: 1, padding: '12px 10px', overflowY: 'auto' },
    navSection: { fontSize: '10px', fontWeight: '700', color: 'rgba(255,255,255,0.25)', padding: '12px 8px 6px', textTransform: 'uppercase', letterSpacing: '0.08em' },
    navLink: (active) => ({
      display: 'flex', alignItems: 'center', gap: '10px',
      width: '100%', padding: '9px 10px', borderRadius: '8px',
      fontSize: '13px', fontWeight: active ? '600' : '400',
      color: active ? '#fff' : 'rgba(255,255,255,0.55)',
      background: active ? 'rgba(232,92,53,0.35)' : 'transparent',
      border: active ? '1px solid rgba(232,92,53,0.4)' : '1px solid transparent',
      cursor: 'pointer', marginBottom: '2px', textAlign: 'left',
      transition: 'all 0.15s',
    }),
    navIcon: { fontSize: '15px', width: '18px', textAlign: 'center' },
    bottom: {
      padding: '14px 14px',
      borderTop: '1px solid rgba(255,255,255,0.08)',
    },
    userRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
    avatar: {
      width: '30px', height: '30px', borderRadius: '50%',
      background: 'var(--signal)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '11px', fontWeight: '700', color: '#4A1B0C', flexShrink: 0,
    },
    userName: { fontSize: '12px', fontWeight: '600', color: '#fff' },
    userRole: { fontSize: '10px', color: roleColor },
    signOut: {
      width: '100%', padding: '7px', borderRadius: '7px', fontSize: '12px',
      background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)',
      border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer',
    },
    main: { flex: 1, overflow: 'auto', background: 'var(--bg)' },
  };

  const initials = (user?.name || 'U').split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();

  return (
    <>
    <style>{mobileCSS}</style>
    <div style={s.shell} className="ms-shell">
      <div style={s.sidebar} className="ms-sidebar">
        <div style={s.logoArea} className="ms-logo-area">
          <div style={s.logoRow} onClick={() => navigate('/dashboard')}>
            <div style={s.logoMark}>
                {stationLogo
                  ? <img src={stationLogo} alt="logo" style={{width:'22px',height:'22px',objectFit:'contain',borderRadius:'4px'}}/>
                  : 'M'}
              </div>
            <span style={s.logoText}>MultiStream</span>
          </div>
          <div style={s.logoSub}>Dennery FM 88.9</div>
          <div style={s.liveBadge}>
            <div style={s.liveDot} />
            {liveStatus?.live ? 'ON AIR' : 'Standby'}
          </div>
        </div>

        <div style={s.nav} className="ms-nav">
          <div style={s.navSection} className="ms-nav-section">Menu</div>
          {navItems.map(item => (
            <button key={item.path} style={s.navLink(isActive(item.path))} className="ms-nav-item" onClick={() => item.newTab ? window.open(item.path, '_blank') : navigate(item.path)}>
              <span style={s.navIcon} className="ms-nav-icon">{item.icon}</span>
              <span className="ms-nav-label">{item.label}</span>
            </button>
          ))}
        </div>

        <div style={s.bottom} className="ms-bottom">
          <div style={s.userRow}>
            <div style={s.avatar}>{initials}</div>
            <div>
              <div style={s.userName}>{user?.name}</div>
              <div style={s.userRole}>{user?.role}</div>
            </div>
          </div>
          <button style={s.signOut} onClick={logout}>Sign out</button>
        </div>
      </div>

      <div style={s.main} className="ms-main">{children}</div>
    </div>
    </>
  );
}
